import type { Theme } from '@emotion/react'
import { ChevronDownIcon, MicrophoneIcon, XIcon } from '@moia-dev/pace-icons'
import {
  ActionMenu,
  Checkbox,
  DateRangePicker,
  Icon,
  IconButton,
  Option,
  Select,
  Text,
  useActionMenu,
} from '@moia-dev/pace-web'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActiveLayersDot } from '../components/ActiveLayersDot'
import {
  MAP_CONTROLS_MENU_Z_INDEX,
  useMapControlsSide,
} from '../components/mapControlsSide'
import {
  ANNOTATION_SEVERITY_COLORS,
  annotationSeverityLabel,
  KNOWN_ANNOTATION_SEVERITIES,
} from './annotation-labels'
import { ANNOTATION_PROXIMITY_RADII_M } from './annotation-utils'
import type { AnnotationsState } from './useAnnotationsState'

type SectionKey = 'severity' | 'dateRange'

/**
 * Width of the dropdown's content, inside the ActionMenu's own padding. Sized
 * so the date range picker fits two full dates, its arrow and its calendar
 * glyph on one row without any of them touching.
 */
const MENU_CONTENT_WIDTH_PX = 296
/** Height of one checkbox row, and so the rhythm of the whole menu. */
const ROW_MIN_HEIGHT_PX = 36
/** Height of a section heading row ("Annotations", "Severity", …). */
const HEADING_MIN_HEIGHT_PX = 28
/** The severity circle in front of each severity row. */
const SEVERITY_AVATAR_PX = 20
const SEVERITY_GLYPH_PX = 12

/**
 * Re-spaces pace's DateRangePicker for a narrow menu. Its row is: date input,
 * arrow icon, date input, calendar button. pace pads each date twice (the
 * picker's `--space-2` and the Input's own `--space-3` a side) and gives it an
 * 80px min-width, which in this width pushes the native date text over the
 * arrow and the calendar glyph.
 *
 * Here the two dates share the free space equally with their text centred, and
 * the arrow and calendar keep fixed gaps of their own. `min-width: 0` lets a
 * long locale format clip inside its own half rather than overlap a neighbour.
 *
 * The date selectors carry `:has(> input[type="date"])` to outrank pace's
 * `.dateRangePicker .datePicker` padding rule.
 */
const dateRangePickerCss = (theme: Theme) => ({
  '& > div:has(> input[type="date"])': {
    flex: '1 1 0',
    minWidth: 0,
    padding: `0 ${theme.space.Space1}`,
  },
  '& input[type="date"]': {
    minWidth: 0,
    textAlign: 'center' as const,
  },
  // The arrow between the two dates.
  '& > div:not(:has(input))': {
    flexShrink: 0,
    margin: `0 ${theme.space.Space1}`,
  },
  // The calendar glyph. The clear-X overlay below lines up with its right
  // edge, which this margin leaves where it was.
  '& > button': {
    flexShrink: 0,
    marginLeft: theme.space.Space2,
  },
})

/**
 * A band of the menu. The ActionMenu brings `--space-1` of padding of its own,
 * so `--space-3` here puts the content `--space-4` in from the card's edge.
 */
const MenuSection = ({ children }: { children: ReactNode }) => (
  <div
    css={theme => ({
      display: 'flex',
      flexDirection: 'column',
      padding: theme.space.Space3,
    })}
  >
    {children}
  </div>
)

/**
 * A rule between two sections, run out to the card's edges: the negative margin
 * cancels the ActionMenu's own padding.
 */
const MenuDivider = () => (
  <div
    aria-hidden
    css={theme => ({
      height: 1,
      flexShrink: 0,
      margin: `0 -${theme.space.Space1}`,
      backgroundColor: theme.semantic.ColorAccessoryReduced,
    })}
  />
)

const MenuHeading = ({ children }: { children: ReactNode }) => (
  <div
    css={{
      display: 'flex',
      alignItems: 'center',
      minHeight: HEADING_MIN_HEIGHT_PX,
    }}
  >
    <Text size="lg" weight="bold">
      {children}
    </Text>
  </div>
)

/**
 * One pace checkbox, laid out the way the menu draws its rows: label on the
 * left, box on the far right, so a click anywhere across the text toggles it.
 *
 * pace's Checkbox always puts the box first; its `<label>` is reversed here to
 * send the box to the end. Still pace's own input underneath, so the box keeps
 * pace's look and focus ring and stays named by its label.
 */
const CheckboxRow = ({
  label,
  checked,
  onChange,
  leading,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  /** Drawn in front of the label, e.g. the severity circle. */
  leading?: ReactNode
}) => (
  <div
    css={theme => ({
      display: 'flex',
      alignItems: 'center',
      gap: theme.space.Space2,
      minHeight: ROW_MIN_HEIGHT_PX,
    })}
  >
    {leading}
    <Checkbox
      label={label}
      checked={checked}
      onChange={event => onChange(event.target.checked)}
      css={{
        flex: 1,
        minWidth: 0,
        '& > label': {
          flexDirection: 'row-reverse',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
      }}
    />
  </div>
)

/**
 * The circle in front of a severity row: the marker's colour with a white mic,
 * so the filter doubles as the map's legend. Decorative: the row's label names
 * the severity.
 */
const SeverityAvatar = ({ color }: { color: string }) => (
  <span
    aria-hidden
    css={theme => ({
      width: SEVERITY_AVATAR_PX,
      height: SEVERITY_AVATAR_PX,
      flexShrink: 0,
      borderRadius: theme.radius.RadiusFull,
      backgroundColor: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    })}
  >
    {/* Not wrapped in pace's Icon: that forces the content colour and its
        smallest size is 16px, too big for this circle. */}
    <MicrophoneIcon
      width={SEVERITY_GLYPH_PX}
      height={SEVERITY_GLYPH_PX}
      css={{ color: 'white' }}
    />
  </span>
)

/**
 * A section whose body folds away under its heading. Both sections here always
 * start expanded: the severity and date filters are the reason this dropdown
 * gets opened, so hiding them by default would just add an extra click for most
 * visits.
 */
const CollapsibleSection = ({
  title,
  expanded,
  onToggle,
  children,
}: {
  title: string
  expanded: boolean
  onToggle: () => void
  children: ReactNode
}) => (
  <MenuSection>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: HEADING_MIN_HEIGHT_PX,
        padding: 0,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        '&:hover': { opacity: 0.7 },
      }}
    >
      <Text size="lg" weight="bold">
        {title}
      </Text>
      {/* Points up while the section is open, down once it is folded away. */}
      <Icon
        size={16}
        css={{
          flexShrink: 0,
          transition: 'transform 150ms ease',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      >
        <ChevronDownIcon />
      </Icon>
    </button>
    <div
      css={{
        display: 'grid',
        gridTemplateRows: expanded ? '1fr' : '0fr',
        transition: 'grid-template-rows 200ms ease',
      }}
    >
      {/* `inert` while folded, so the hidden checkboxes drop out of the tab
          order rather than taking focus somewhere the user cannot see. */}
      <div css={{ overflow: 'hidden', minHeight: 0 }} inert={!expanded}>
        {children}
      </div>
    </div>
  </MenuSection>
)

export type AnnotationsButtonProps = {
  /** The state object from {@link useAnnotationsState}, shared with the map layer. */
  state: AnnotationsState
  /**
   * Dropdown open state, owned by the caller so a map rail can make this button
   * and its Layers sibling mutually exclusive (route-builder derives both from
   * one `openMapMenu` value). Leave both out for a self-contained button that
   * manages its own open state.
   */
  isOpen?: boolean
  setIsOpen?: Dispatch<SetStateAction<boolean>>
  /**
   * Offer the "near this route" proximity filter. Off by default: a map with no
   * route (a trip overview, a fleet-wide view) has nothing to be near, so the
   * control would read as on while changing nothing.
   */
  showNearRouteFilter?: boolean
  /**
   * Offer the recorded-date-range filter. On by default. Turn it off on a map
   * whose notes are already scoped to one trip (trip-history's trip detail),
   * where a date range could only narrow the trip down to itself.
   */
  showDateRangeFilter?: boolean
}

/**
 * Map-rail toggle for the driver-voice-note ("mic sign") layer, rendered beside
 * the Layers button via `MapControls`' `extraButtons` slot.
 *
 * Drawn like that Layers button: a white button that turns primary while its
 * menu is open, with the same blue dot while its layer is on the map.
 *
 * Takes its state as a prop rather than reading a context, so the same button
 * works on any map: the caller creates one {@link useAnnotationsState} and hands
 * it to both this button and `useAnnotationMapLayer`, and the count shown here
 * is by construction the count the map is drawing.
 */
export const AnnotationsButton = ({
  state,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
  showNearRouteFilter = false,
  showDateRangeFilter = true,
}: AnnotationsButtonProps) => {
  const { t } = useTranslation()
  const {
    allAnnotations,
    visibleAnnotations,
    nearRouteTotal,
    hasRoute,
    showAnnotations,
    setShowAnnotations,
    severityFilter,
    setSeverityFilter,
    range,
    setRange,
    showHeatmap,
    setShowHeatmap,
    nearRouteOnly,
    setNearRouteOnly,
    nearRouteRadiusM,
    setNearRouteRadiusM,
    loadingMore,
    truncated,
  } = state

  // Uncontrolled fallback. Declared unconditionally (hooks can't be
  // conditional) and simply ignored when the caller controls the open state.
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false)
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen
  // Typed as a full Dispatch<SetStateAction<boolean>> (not just `(open:
  // boolean) => void`) because useActionMenu's setIsOpen prop requires that
  // shape — it may call it with an updater function, not only a plain boolean.
  const setIsOpen = controlledSetIsOpen ?? setUncontrolledIsOpen

  // Both filter sections default to expanded; collapsing one is a per-session
  // convenience, not a persisted preference, so plain local state is enough.
  const [expandedSections, setExpandedSections] = useState<
    Partial<Record<SectionKey, boolean>>
  >({})
  const toggleSection = (key: SectionKey) =>
    setExpandedSections(prev => ({ ...prev, [key]: !(prev[key] ?? true) }))

  // Opens toward the rail's edge, the way the Layers panel does: on the
  // right-hand rail (route builder, its Assign Trip modal) a `bottom-start`
  // menu grows rightwards from the button and runs off the map.
  const railSide = useMapControlsSide()
  const { getReferenceProps, ...actionMenuProps } = useActionMenu({
    isOpen,
    setIsOpen,
    placement: railSide === 'right' ? 'bottom-end' : 'bottom-start',
  })

  const toggleSeverity = (severity: string) => {
    setSeverityFilter(
      severityFilter.includes(severity)
        ? severityFilter.filter(s => s !== severity)
        : [...severityFilter, severity],
    )
  }

  return (
    <>
      <div css={{ position: 'relative' }}>
        <IconButton
          ref={actionMenuProps.refs.setReference}
          size="compact"
          variant={isOpen ? 'primary' : 'secondary'}
          icon={<MicrophoneIcon />}
          label={t('mapAnnotations_button_label')}
          {...getReferenceProps()}
        />
        {showAnnotations && <ActiveLayersDot />}
      </div>
      {/* Above the 10001 backdrop of the modals the route builder's map opens
          in, which would otherwise cover a menu at pace's default 9999. */}
      <ActionMenu {...actionMenuProps} zIndex={MAP_CONTROLS_MENU_Z_INDEX}>
        <div
          css={{
            width: MENU_CONTENT_WIDTH_PX,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MenuSection>
            <MenuHeading>{t('mapAnnotations_button_label')}</MenuHeading>
            {/* The count is the whole layer, not the filtered subset: notes sit
                wherever they were recorded and are deliberately left out of the
                camera fit, so without it there is no way to tell "none in this
                date range" from "none anywhere". */}
            <CheckboxRow
              label={t('mapAnnotations_layer_toggle', {
                total: allAnnotations.length,
              })}
              checked={showAnnotations}
              onChange={setShowAnnotations}
            />
            {/* The count above grows while older pages arrive, and stops short
                when the page cap is hit; both say so rather than leave the
                number looking final. */}
            {(loadingMore || truncated) && (
              <Text size="sm" color="reduced">
                {loadingMore
                  ? t('mapAnnotations_layer_loadingMore')
                  : t('mapAnnotations_layer_truncated')}
              </Text>
            )}
            {showAnnotations && (
              <>
                <CheckboxRow
                  label={t('mapAnnotations_layer_heatmapToggle')}
                  checked={showHeatmap}
                  onChange={setShowHeatmap}
                />
                {showNearRouteFilter && (
                  <>
                    <CheckboxRow
                      label={t('mapAnnotations_layer_nearRouteToggle')}
                      checked={nearRouteOnly}
                      onChange={setNearRouteOnly}
                    />
                    {nearRouteOnly && (
                      <div
                        css={theme => ({
                          display: 'flex',
                          flexDirection: 'column',
                          gap: theme.space.Space2,
                          paddingBottom: theme.space.Space1,
                        })}
                      >
                        <Select<string>
                          aria-label={t(
                            'mapAnnotations_layer_nearRouteRadiusLabel',
                          )}
                          size="compact"
                          value={String(nearRouteRadiusM)}
                          onChange={value => {
                            if (value) setNearRouteRadiusM(Number(value))
                          }}
                          renderValue={value =>
                            t('mapAnnotations_layer_nearRouteRadius', {
                              meters: value,
                            })
                          }
                          options={ANNOTATION_PROXIMITY_RADII_M.map(meters => (
                            <Option<string> key={meters} value={String(meters)}>
                              {t('mapAnnotations_layer_nearRouteRadius', {
                                meters,
                              })}
                            </Option>
                          ))}
                        />
                        {/* Says outright when the filter is inert: the
                            proximity predicate has no polyline to measure
                            against until a route exists, so the layer is still
                            the full fleet-wide set — a filter that reads as on
                            while changing nothing is worse than no filter. */}
                        <Text size="sm" color="reduced">
                          {hasRoute
                            ? t('mapAnnotations_layer_nearRouteCount', {
                                shown: visibleAnnotations.length,
                                total: nearRouteTotal,
                              })
                            : t('mapAnnotations_layer_nearRouteNoRoute')}
                        </Text>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </MenuSection>

          {showAnnotations && (
            <>
              <MenuDivider />
              <CollapsibleSection
                title={t('mapAnnotations_dropdown_severityHeading')}
                expanded={expandedSections.severity ?? true}
                onToggle={() => toggleSection('severity')}
              >
                {KNOWN_ANNOTATION_SEVERITIES.map(severity => (
                  <CheckboxRow
                    key={severity}
                    // Same colour the marker on the map renders in, so the rows
                    // double as a legend for why some mic pins are a different
                    // colour, right where the filter that hides them lives.
                    leading={
                      <SeverityAvatar
                        color={ANNOTATION_SEVERITY_COLORS[severity]}
                      />
                    }
                    label={annotationSeverityLabel(t, severity)}
                    checked={severityFilter.includes(severity)}
                    onChange={() => toggleSeverity(severity)}
                  />
                ))}
              </CollapsibleSection>

              {showDateRangeFilter && (
                <>
                  <MenuDivider />
                  <CollapsibleSection
                    title={t('mapAnnotations_layer_rangeLabel')}
                    expanded={expandedSections.dateRange ?? true}
                    onToggle={() => toggleSection('dateRange')}
                  >
                    <div css={theme => ({ paddingTop: theme.space.Space1 })}>
                      {/*
                        When a range is set, an X *replaces* the picker's own
                        calendar glyph in place instead of appearing as an extra
                        button beside it — so clearing never widens/shifts the
                        row (the earlier expand/collapse button did, and its
                        close frame animated inconsistently). pace's
                        `DateRangePicker` exposes no clear or `trailing` prop of
                        its own (it renders the calendar icon internally), so
                        the only way to swap that glyph is to overlay the X on
                        top of it. The overlay carries the picker's exact
                        `--color-input-default` background so it masks the
                        calendar icon underneath rather than sitting on top of
                        it as a double-glyph, and is only mounted when a range
                        exists, so the calendar icon (and its open-calendar
                        click) is untouched otherwise.

                        Geometry is matched to the picker's calendar glyph
                        exactly rather than eyeballed: pace renders that glyph
                        as a 20px `Icon` in a *zero-padding* UnstyledButton,
                        sitting `--space-3` (12px) in from the picker's right
                        border (the picker's own
                        `padding: 0 var(--space-3) 0 0`). So the overlay
                        right-aligns a 20px `Icon` with the same `--space-3`
                        right padding, giving the X the same right edge and size
                        as the calendar glyph. A pace `IconButton` is
                        deliberately NOT used here — its built-in `--space-2`
                        (8px) padding would push the glyph ~8px inward and off
                        the calendar's position — so this mirrors pace's own
                        zero-padding button for this affordance.
                      */}
                      <div css={{ position: 'relative' }}>
                        <DateRangePicker
                          size="compact"
                          allowOpenEnd
                          value={range}
                          onChange={setRange}
                          css={dateRangePickerCss}
                        />
                        {range?.from ? (
                          <div
                            css={{
                              position: 'absolute',
                              // Inset by the picker's 1px border so the mask
                              // never paints over its rounded corner.
                              // Right-align the X and pad it by the picker's
                              // own --space-3 so its right edge lands where the
                              // calendar glyph's does.
                              top: 1,
                              right: 1,
                              bottom: 1,
                              paddingRight: 'var(--space-3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              background: 'var(--color-input-default)',
                              borderTopRightRadius: 'var(--radius-medium)',
                              borderBottomRightRadius: 'var(--radius-medium)',
                            }}
                          >
                            <button
                              type="button"
                              aria-label={t('mapAnnotations_layer_rangeClear')}
                              onClick={() => setRange(undefined)}
                              css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'var(--color-content-default)',
                              }}
                            >
                              <Icon size={20}>
                                <XIcon />
                              </Icon>
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </CollapsibleSection>
                </>
              )}
            </>
          )}
        </div>
      </ActionMenu>
    </>
  )
}
